/* =========================================================================
   Northern Raised — Custom Box Builder · Shopify / Recharge adapter
   - Loads bundle data through the Recharge Storefront JS SDK
     (recharge.bundleData.loadBundleData → app proxy /tools/recurring/bundle-data/:id)
   - Normalises it into the shape the NRBox engine expects
   - Merges product metafields injected by the Liquid section (window.NRBoxMeta)
   - EDIT IN PLACE: reads the cart, hydrates an existing box back into the builder,
     and replaces it atomically on save so customers never end up with two boxes.
   Requires: nb-builder.js (NRBox engine) and the Recharge SDK script.
   ========================================================================= */
(function (global) {
  'use strict';
  var cfg = global.NRBoxConfig || {};
  var meta = global.NRBoxMeta || { pkg: {}, cls: {}, mult: {}, farm: {}, oz: {} };
  var mount = document.getElementById(cfg.mountId || 'nb-app');

  var EDIT_PARAM = cfg.editParam || 'edit';
  var EXTRA_PROP = '_nb_extra';           // tags one-time extras to the box that added them
  var ROOT = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';

  /* ---------- helpers ---------- */
  function txt(html) { var d = document.createElement('div'); d.innerHTML = html || ''; return (d.textContent || '').replace(/\s+/g, ' ').trim(); }
  function abs(u) { if (!u) return ''; if (u.indexOf('//') === 0) return 'https:' + u; return u; }
  function sized(u, w) { u = abs(u); if (!u) return ''; return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }
  function cents(n) {
    if (n == null || n === '') return 0;
    if (typeof n === 'string' && n.indexOf('.') > -1) { var f = parseFloat(n); return isNaN(f) ? 0 : Math.round(f * 100) / 100; }
    var v = Number(n); return isNaN(v) ? 0 : Math.round(v) / 100;
  }
  function pick() { for (var i = 0; i < arguments.length; i++) { var v = arguments[i]; if (v !== undefined && v !== null && v !== '') return v; } return null; }
  function amount(o) { if (o == null) return null; if (typeof o === 'number' || typeof o === 'string') return o; return pick(o.min, o.amount, o.min_variant_price && (o.min_variant_price.amount != null ? o.min_variant_price.amount : o.min_variant_price)); }
  // Selling-plan frequency: tolerates options as [{name,value}] or ['1-week'], and falls back to the plan name
  function planWeeks(sp) {
    if (!sp) return null;
    var cand = [], o = (sp.options || []);
    if (Array.isArray(o)) o.forEach(function (x) { cand.push(typeof x === 'string' ? x : String((x && (x.value != null ? x.value : x.name)) || '')); });
    cand.push(String(sp.name || ''), String(sp.description || ''));
    for (var i = 0; i < cand.length; i++) {
      var m = /(\d+)\s*-?\s*(day|week|month)/i.exec(cand[i]);
      if (m) { var n = +m[1], u = m[2].toLowerCase(); return u === 'week' ? n : u === 'day' ? Math.max(1, Math.round(n / 7)) : n * 4; }
    }
    return null;
  }
  function whenSdk(cb, tries) { tries = tries || 0; if (global.recharge && global.recharge.bundleData) return cb(); if (tries > 200) return cb(new Error('Recharge SDK not available')); setTimeout(function () { whenSdk(cb, tries + 1); }, 50); }
  function param(name) { try { return new URLSearchParams(global.location.search).get(name); } catch (e) { return null; } }
  function jsonFetch(path, opts) {
    return fetch(ROOT + path, Object.assign({ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }, opts || {}))
      .then(function (res) { return res.json().then(function (body) { if (!res.ok) throw new Error(body.description || body.message || ('HTTP ' + res.status)); return body; }); });
  }
  function fail(msg) {
    console.error('[NRBox] ' + msg);
    mount.innerHTML = '<div class="nb nb-fallback"><div class="nb-fallback__card"><h2>The box builder is taking a moment.</h2><p>Please refresh, or <a href="' + (cfg.fallbackUrl || '?view=recharge-bundle') + '">use the classic builder</a>.</p></div></div>';
  }

  /* ---------- saving overlay ---------- */
  var overlay = null;
  function busy(on, title, sub) {
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'nb-saving';
      overlay.innerHTML = '<div class="nb-saving__card"><span class="nb-saving__spin"></span><b data-t></b><span data-s></span></div>';
      mount.appendChild(overlay);
    }
    overlay.querySelector('[data-t]').textContent = title || 'Updating your box…';
    overlay.querySelector('[data-s]').textContent = sub || 'One moment — we’re replacing the box in your cart.';
    overlay.classList.toggle('is-on', !!on);
    if (on) overlay.classList.remove('is-error');
  }

  /* A cart write failed: keep it on screen with the real reason and a retry, instead of a 3s toast. */
  function failCard(msg, retry) {
    busy(true, 'We couldn’t add your box', msg);
    overlay.classList.add('is-error');
    var card = overlay.querySelector('.nb-saving__card');
    if (!card.querySelector('[data-nb-retry]')) {
      var row = document.createElement('div');
      row.className = 'nb-saving__acts';
      row.innerHTML = '<button type="button" class="nb-saving__btn" data-nb-retry>Try again</button>' +
                      '<button type="button" class="nb-saving__btn nb-saving__btn--ghost" data-nb-dismiss>Back to my box</button>';
      card.appendChild(row);
    }
    card.querySelector('[data-nb-retry]').onclick = function () { retry(); };
    card.querySelector('[data-nb-dismiss]').onclick = function () { busy(false); };
  }

  /* ---------- normalise Recharge bundle data ---------- */
  function normalise(bd) {
    var settings = bd.bundle_settings || {};

    /* The SDK's PublicBundleData puts variants at the top level; older/raw app-proxy
       payloads nested them under bundle_settings and called the collection bindings
       "option_sources". Accept both so an SDK bump can't break this again. */
    var variants = (bd.variants && bd.variants.length ? bd.variants : (settings.variants || []));
    var bVariant = variants.filter(function (v) { return v.available_for_sale !== false && v.enabled !== false; })
      .sort(function (a, b) { return (a.position || 0) - (b.position || 0); })[0] || variants[0] || {};

    var range = (bVariant.ranges || [])[0] || {};
    var minItems = Number(pick(range.min, range.quantity_min, cfg.minItems, 6));
    var maxItemsV = pick(range.max, range.quantity_max);
    var maxItems = maxItemsV == null ? null : Number(maxItemsV);

    /* ---- Buy More Save More ---- */
    var tiers = [];
    var tdList = (bd.incentives && bd.incentives.tiered_discounts) || settings.tiered_discounts || bd.tiered_discounts || [];
    var td = tdList[0] || {};
    ((td.tiers) || []).forEach(function (t) {
      t = t.tier || t;
      var type = String(pick(t.discount_type, t.type, 'percentage'));
      if (!/percent/i.test(type)) return;
      var qty = Number(pick(t.condition_quantity_gte, t.condition_value, t.quantity, t.min_quantity, t.threshold, 0));
      var pct = Number(pick(t.discount_value, t.value, t.percentage, t.amount, 0));
      if (qty > 0 && pct > 0) tiers.push({ qty: qty, pct: pct });
    });
    tiers.sort(function (a, b) { return a.qty - b.qty; });

    /* ---- delivery frequencies ---- */
    var plans = [];
    (bd.selling_plan_groups || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (sp) {
        var w = planWeeks(sp) || planWeeks(g);          // group name ("3 week subscription") is a good fallback
        if (w) plans.push({ id: String(sp.id), weeks: w, name: sp.name || g.name });
      });
    });
    plans.sort(function (a, b) { return a.weeks - b.weeks; });
    var dedup = {}; plans = plans.filter(function (p) { if (dedup[p.id]) return false; dedup[p.id] = 1; return true; });
    var defaultPlanId = String(pick(bd.default_selling_plan_id,
      settings.layout_settings && settings.layout_settings.defaultFrequency,
      plans[0] && plans[0].id) || '');

    /* ---- categories ---- */
    var sources = (bVariant.collections || bVariant.option_sources || []).slice()
      .sort(function (a, b) { return (a.position || 0) - (b.position || 0); });
    var farms = {}, products = [], collections = [], seen = {};
    var freeHandle = cfg.freeCollectionHandle || '';

    sources.forEach(function (src) {
      var cid = String(pick(src.id, src.option_source_id, src.collection_id, ''));
      var c = bd.collections && bd.collections[cid]; if (!c) return;
      var sMin = Number(pick(src.min, src.quantity_min, 0));
      var sMaxV = pick(src.max, src.quantity_max);
      var sMax = sMaxV == null ? null : Number(sMaxV);
      var isFree = (freeHandle && c.handle === freeHandle) || sMax === 1;

      var col = {
        id: cid, handle: c.handle || '', title: String(c.title || '').replace(/\*+$/, '').trim(),
        min: sMin, max: sMax,
        blurb: txt(c.description || (cfg.collectionBlurbs || {})[c.handle] || ''),
        image: (cfg.collectionImages || {})[c.handle] || '', farms: []
      };

      (c.products || []).forEach(function (p) {
        var pid = String(p.id), v = (p.variants || [])[0] || {};
        var farm = meta.farm[pid] || null;
        if (farm && farm.name) {
          farms[farm.name] = { name: farm.name, location: farm.location || '', description: farm.description || '', image: abs(farm.image || '') };
          if (col.farms.indexOf(farm.name) < 0) col.farms.push(farm.name);
        }
        var img = abs(p.featured_image || (p.images || [])[0] || '');
        var price = cents(pick(v.price, amount(p.price_range), p.price, 0));
        var capRaw = pick(v.compare_at_price, amount(p.compare_at_price_range));
        var cap = capRaw == null ? null : cents(capRaw);
        products.push({
          id: pid, vid: String(v.id || ''), handle: p.handle, title: p.title,
          price: price, cap: (cap && cap > price) ? cap : null,
          avail: p.available_for_sale !== false && p.available !== false && v.available_for_sale !== false && v.available !== false,
          img: sized(img, 720), body: txt(p.description).slice(0, 700), cid: cid,
          pkg: meta.pkg[pid] || '', cls: meta.cls[pid] || [], mult: meta.mult[pid] || 1,
          farm: farm && farm.name ? farm.name : null, oz: meta.oz[pid] || 0,
          plans: (v.selling_plan_allocations || []).map(function (a) {
            return { id: String((a.selling_plan && a.selling_plan.id) || a.selling_plan_id || ''), weeks: planWeeks(a.selling_plan) };
          }).filter(function (x) { return x.id; })
        });
        seen[pid] = true;
      });

      if (!col.image && col.farms.length && farms[col.farms[0]].image) col.image = farms[col.farms[0]].image.replace(/([?&])width=\d+/, '$1width=1400');
      if (!col.image) col.image = cfg.boxPhoto || '';
      if (isFree) col.max = 1;
      collections.push(col);
    });

    /* ---- one-time extras (cross-sells not already in the bundle) ---- */
    var extras = [];
    ((bd.cross_sells && bd.cross_sells.products) || (bd.addons && bd.addons.products) || []).forEach(function (p) {
      var pid = String(p.id); if (seen[pid]) return;
      var v = (p.variants || [])[0] || {};
      extras.push({
        id: pid, vid: String(v.id || ''), handle: p.handle, title: String(p.title || '').replace(/^1 x /i, ''),
        price: cents(pick(v.price, amount(p.price_range), p.price, 0)),
        img: sized(abs(p.featured_image || (p.images || [])[0] || ''), 480),
        body: txt(p.description).slice(0, 300), pkg: meta.pkg[pid] || '',
        /* Add-ons recur with the box now, so they need a selling plan of their own — Shopify will
           not subscribe a line without one. Captured the same way as a bundle product's. */
        plans: (v.selling_plan_allocations || []).map(function (a) {
          return { id: String((a.selling_plan && a.selling_plan.id) || a.selling_plan_id || ''), weeks: planWeeks(a.selling_plan) };
        }).filter(function (x) { return x.id; }),
        /* Shopify's flag, recorded for diagnostics only. It is NOT the same thing as Recharge's
           per-product purchase-option rule, so nothing filters on it. */
        subOnly: !!p.requires_selling_plan
      });
    });

    var byHandle = {}; products.forEach(function (p) { byHandle[p.handle] = p; });
    var presets = (cfg.presets || []).map(function (pr) {
      var items = {}; (pr.items || []).forEach(function (h) { var p = byHandle[String(h).trim()]; if (p) items[p.id] = 1; });
      var more = (pr.more || []).map(function (h) { var p = byHandle[String(h).trim()]; return p ? p.id : null; }).filter(Boolean);
      return Object.keys(items).length ? { key: pr.key, title: pr.title, tag: pr.tag, blurb: pr.blurb, items: items, more: more } : null;
    }).filter(Boolean);
    var collage = (cfg.collage || []).map(function (h) { var p = byHandle[String(h).trim()]; return p ? p.id : null; }).filter(Boolean);
    if (!collage.length) collage = products.filter(function (p) { return p.price > 0; }).slice(0, 12).map(function (p) { return p.id; });

    var bundleVariantId = String(pick(bVariant.external_variant_id, bVariant.id, bd.default_variant_id, (variants[0] || {}).id, '') || '');

    return {
      bundle: {
        productId: String(bd.id), variantId: bundleVariantId, handle: bd.handle, title: bd.title,
        minItems: minItems, maxItems: maxItems, tiers: tiers, plans: plans,
        defaultPlanId: defaultPlanId, portionOz: cfg.portionOz || 6
      },
      collections: collections, farms: farms, products: products, extras: extras,
      presets: presets, collage: collage.slice(0, 12), boxPhoto: cfg.boxPhoto || '',
      personalize: cfg.personalize || null
    };
  }

  /* =======================================================================
     CART: read, group, hydrate, replace
     ======================================================================= */

  /** Group the cart into boxes built by this widget. */
  function readBoxes(cart, data) {
    var byVid = {}; data.products.forEach(function (p) { byVid[p.vid] = p; });
    var extraByVid = {}; (data.extras || []).forEach(function (p) { extraByVid[p.vid] = p; });
    var groups = {}, order = [];
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {}, bid = props._rc_bundle;
      if (!bid) return;
      var parentOk = props._rc_bundle_parent ? props._rc_bundle_parent === data.bundle.handle : true;
      var variantOk = props._rc_bundle_variant ? String(props._rc_bundle_variant) === String(data.bundle.variantId) : true;
      var known = !!byVid[String(item.variant_id)];
      if (!(parentOk && variantOk) && !known) return;
      if (!groups[bid]) { groups[bid] = { id: bid, lines: [], extras: [], count: 0, total: 0, thumbs: [], planId: null, unknown: 0 }; order.push(bid); }
      var g = groups[bid], p = byVid[String(item.variant_id)];
      g.lines.push({ key: item.key, variantId: String(item.variant_id), quantity: item.quantity, product: p || null, collectionId: props._rc_bundle_collection_id ? String(props._rc_bundle_collection_id) : (p ? p.cid : null) });
      g.count += item.quantity; g.total += (item.final_line_price != null ? item.final_line_price : item.line_price) / 100;
      if (!p) g.unknown++;
      if (item.image && g.thumbs.length < 4) g.thumbs.push(item.image.replace(/(\.(?:png|jpe?g|webp))(\?|$)/i, '_120x$1$2'));
      if (!g.planId && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) g.planId = String(item.selling_plan_allocation.selling_plan.id);
    });
    // one-time extras tagged to a box by this widget
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {}, tag = props[EXTRA_PROP];
      if (!tag || !groups[tag]) return;
      var p = extraByVid[String(item.variant_id)]; if (!p) return;
      groups[tag].extras.push({ key: item.key, variantId: String(item.variant_id), quantity: item.quantity, product: p });
      groups[tag].total += (item.final_line_price != null ? item.final_line_price : item.line_price) / 100;
    });
    return order.map(function (id) { return groups[id]; });
  }

  /** Turn a cart box into builder state. */
  function toState(box, data) {
    var qty = {}, extras = {}, planId = null, dropped = 0;
    box.lines.forEach(function (l) { if (!l.product) { dropped++; return; } qty[l.product.id] = (qty[l.product.id] || 0) + l.quantity; });
    box.extras.forEach(function (l) { extras[l.product.id] = (extras[l.product.id] || 0) + l.quantity; });
    if (box.planId) {
      // the child's selling plan id differs from the bundle's — match on frequency
      var weeks = null;
      box.lines.some(function (l) {
        if (!l.product) return false;
        var hit = (l.product.plans || []).filter(function (x) { return x.id === box.planId; })[0];
        if (hit && hit.weeks) { weeks = hit.weeks; return true; }
        return false;
      });
      var match = weeks ? data.bundle.plans.filter(function (p) { return p.weeks === weeks; })[0] : data.bundle.plans.filter(function (p) { return p.id === box.planId; })[0];
      if (match) planId = match.id;
    }
    return { qty: qty, extras: extras, planId: planId, dropped: dropped, editing: { bundleId: box.id, lineKeys: box.lines.map(function (l) { return l.key; }), extraKeys: box.extras.map(function (l) { return l.key; }) } };
  }

  /* Name the product in the warning, not its id — whoever reads the console has to find it in
     Recharge admin, and an id is a lookup they shouldn't have to do. */
  function extraTitle(x, data, byId) {
    var p = (byId && (byId[x.pid] || byId[String(x.id)])) ||
      ((data && data.extras) || []).filter(function (e) { return String(e.id) === String(x.pid) || String(e.vid) === String(x.id); })[0];
    return (p && p.title) || x.pid || String(x.id);
  }

  /* An add-on's own selling plan at the box's frequency. Exact frequency first, then the plan the
     box is on, then nothing — never a plan on a different rhythm, which would put the add-on on
     its own charge cycle and undo the whole point. */
  function extraPlan(x, plan, byId) {
    var p = byId[x.pid] || byId[String(x.id)] || null;
    var plans = (p && p.plans) || x.plans || [];
    var byWeeks = plans.filter(function (q) { return q.weeks === plan.weeks; })[0];
    if (byWeeks) return Number(byWeeks.id);
    var byId2 = plans.filter(function (q) { return String(q.id) === String(plan.id); })[0];
    return byId2 ? Number(byId2.id) : null;
  }

  function resolvePlan(p, plan) {
    var byId = (p.plans || []).filter(function (x) { return x.id === plan.id; })[0]; if (byId) return Number(plan.id);
    var byWeeks = (p.plans || []).filter(function (x) { return x.weeks === plan.weeks; })[0]; if (byWeeks) return Number(byWeeks.id);
    return Number(plan.id);
  }

  /** Build the Recharge cart lines for the current selection. */
  function validateSoft(bundle) {
    var b = global.recharge && global.recharge.bundle;
    var fn = b && b.validateDynamicBundle;
    if (typeof fn !== 'function') return Promise.resolve(null);
    var out;
    try { out = fn.call(b, bundle); } catch (e) { console.warn('[NRBox] validateDynamicBundle threw, continuing', e); return Promise.resolve(null); }
    return Promise.resolve(out).then(function (r) {
      if (r === true || r == null) return null;
      if (typeof r === 'string') return r;
      if (typeof r === 'object') {
        var valid = r.valid !== undefined ? r.valid : (r.isValid !== undefined ? r.isValid : undefined);
        if (valid === false) {
          var e = r.error || (Array.isArray(r.errors) ? r.errors.join(', ') : r.errors) || r.message;
          return (typeof e === 'string' && e) ? e : 'Please check your box and try again.';
        }
      }
      return null;   // unrecognised truthy result → never block the add
    }).catch(function (e) { console.warn('[NRBox] validateDynamicBundle rejected, continuing', e); return null; });
  }

  function buildLines(payload, app) {
    var data = app.data, byId = app.byId, plan = app.plan();
    var bundle = {
      externalProductId: data.bundle.productId,
      externalVariantId: data.bundle.variantId,
      selections: payload.bundle.selections.map(function (s) {
        var p = byId[s.externalProductId];
        return { collectionId: s.collectionId, externalProductId: s.externalProductId, externalVariantId: s.externalVariantId, quantity: s.quantity, sellingPlan: resolvePlan(p, plan) };
      })
    };
    /* Recharge's guidance for DYNAMIC bundles is to call getDynamicBundleItems directly and let the
       cart API validate server-side. validateDynamicBundle is undocumented and (like its documented
       sibling validateBundleSelection) resolves asynchronously to { valid, error } — so treat it as an
       advisory pre-check that can never block the add unless it explicitly reports invalid. */
    return validateSoft(bundle).then(function (problem) {
      if (problem) throw new Error(problem);
      return global.recharge.bundle.getDynamicBundleItems(bundle, data.bundle.handle);
    }).then(function (items) {
      var lines = (items || []).slice();
      var bundleId = null;
      lines.some(function (l) { var b = l.properties && l.properties._rc_bundle; if (b) { bundleId = b; return true; } return false; });
      /* Add-ons ride with the box as SUBSCRIPTIONS on the same cadence — Recharge's cross-sells,
         which is what keeps them out of the bundle's item minimum. A cross-sell with no selling
         plan matching the chosen frequency can only go in as a one-time; that is a product setup
         gap, so name the product rather than letting it quietly not repeat. */
      var noPlan = [];
      (payload.extras || []).forEach(function (x) {
        var props = {}; if (bundleId) props[EXTRA_PROP] = bundleId;
        var line = { id: x.id, quantity: x.quantity, properties: props };
        var sp = extraPlan(x, plan, byId);
        if (sp) line.selling_plan = sp;
        else noPlan.push(extraTitle(x, data, byId));
        lines.push(line);
      });
      if (noPlan.length) {
        console.warn('[NRBox] no matching selling plan for add-on(s): ' + noPlan.join(', ') +
          ' — adding them as one-time. Give each product a subscription plan on the box\'s cadences ' +
          'in Shopify/Recharge so they repeat with the box.');
      }
      return { lines: lines, bundleId: bundleId, addonsWithoutPlan: noPlan };
    });
  }

  /** Add the new box, then remove the old one. Never leaves the cart empty. */
  function commit(payload, app) {
    var editing = payload.editing;
    busy(true, editing ? 'Updating your box…' : 'Adding your box…', editing ? 'Replacing the box already in your cart.' : 'One moment.');
    var built;
    return Promise.resolve()
      .then(function () { return buildLines(payload, app); })
      .then(function (b) { built = b; return jsonFetch('cart/add.js', { method: 'POST', body: JSON.stringify({ items: b.lines }) }); })
      .then(function () {
        if (!editing) return null;
        var updates = {};
        (editing.lineKeys || []).forEach(function (k) { updates[k] = 0; });
        (editing.extraKeys || []).forEach(function (k) { updates[k] = 0; });
        if (!Object.keys(updates).length) return null;
        return jsonFetch('cart/update.js', { method: 'POST', body: JSON.stringify({ updates: updates }) });
      })
      .then(function () { return jsonFetch('cart.js'); })
      .then(function (cart) {
        // Safety net: if an old box somehow survived, remove every box that isn't the new one.
        var boxes = readBoxes(cart, app.data);
        var stale = boxes.filter(function (g) { return built.bundleId && g.id !== built.bundleId; });
        if (!editing || !stale.length) return cart;
        var updates = {};
        stale.forEach(function (g) { g.lines.concat(g.extras).forEach(function (l) { updates[l.key] = 0; }); });
        return jsonFetch('cart/update.js', { method: 'POST', body: JSON.stringify({ updates: updates }) });
      })
      .then(function () {
        app.markSaved({ bundleId: built.bundleId, lineKeys: [], extraKeys: [] });
        try { document.dispatchEvent(new CustomEvent('nrbox:added', { detail: Object.assign({ replaced: !!editing, bundleId: built.bundleId }, payload) })); } catch (e) { }
        if (typeof cfg.onAdded === 'function') { busy(false); return cfg.onAdded(payload); }
        var url = cfg.redirect || '/cart';
        global.location.href = url;
      })
      .catch(function (err) {
        console.error('[NRBox] cart write failed', err);
        var msg = (err && err.message) ? err.message : 'Something went wrong — please try again.';
        failCard(msg, function () { commit(payload, app); });
        try { app.toast(msg); } catch (e) { }
      });
  }

  /* Reusable data loader — the customer portal calls this to get the same normalised
     catalogue (tiers, minimum, frequencies, collections) without duplicating normalise(). */
  global.NRBoxAdapter = {
    normalise: normalise,
    load: function (productId) {
      return new Promise(function (resolve, reject) {
        whenSdk(function (err) {
          if (err) return reject(err);
          try {
            /* If the customer portal already initialised the SDK, re-initialising here would
               replace its config (and its logged-in session) mid-flight. */
            if (!global.__nrRechargeInited && cfg.storefrontAccessToken && global.recharge.init) {
              global.recharge.init({ storefrontAccessToken: cfg.storefrontAccessToken });
              global.__nrRechargeInited = true;
            }
          } catch (e) { }
          global.recharge.bundleData.loadBundleData(String(productId || cfg.bundleProductId))
            .then(function (bd) { resolve(normalise(bd)); })
            .catch(reject);
        });
      });
    }
  };

  if (!mount) return;   // portal pages include this file purely for NRBoxAdapter

  /* ---------- boot ---------- */
  whenSdk(function (err) {
    if (err) return fail(err.message);
    try { if (cfg.storefrontAccessToken) global.recharge.init({ storefrontAccessToken: cfg.storefrontAccessToken, appName: 'nr-box-builder', appVersion: '2.2.2' }); } catch (e) { console.warn('[NRBox] recharge.init', e); }

    Promise.all([
      global.recharge.bundleData.loadBundleData(String(cfg.bundleProductId)),
      jsonFetch('cart.js').catch(function () { return { items: [] }; })
    ]).then(function (res) {
      var bd = res[0], cart = res[1];
      var data = normalise(bd);
      global.NRBoxDebug = { raw: bd, data: data };
      if (!data.products.length) {
        var vs = (bd.variants && bd.variants.length ? bd.variants : ((bd.bundle_settings || {}).variants || []));
        return fail('No products returned for bundle ' + cfg.bundleProductId +
          ' — variants:' + vs.length +
          ' bindings:' + (((vs[0] || {}).collections || (vs[0] || {}).option_sources || []).length) +
          ' collections:' + Object.keys(bd.collections || {}).length +
          '. Inspect window.NRBoxDebug.raw');
      }

      var boxes = readBoxes(cart, data);
      var wanted = param(EDIT_PARAM);
      var target = null;
      if (wanted) target = boxes.filter(function (g) { return g.id === wanted; })[0] || boxes[boxes.length - 1] || null;
      if (!target && boxes.length) {
        var newest = boxes[boxes.length - 1];
        data.existingBox = { id: newest.id, count: newest.count, total: newest.total, thumbs: newest.thumbs, planWeeks: null };
        var st = toState(newest, data);
        if (st.planId) { var pl = data.bundle.plans.filter(function (p) { return p.id === st.planId; })[0]; if (pl) data.existingBox.planWeeks = pl.weeks; }
      }

      mount.innerHTML = '';
      var app = new global.NRBox(mount, data, {
        homeUrl: cfg.exitUrl || '/',
        /* In the CART flow we cannot know a discount code before checkout, and Recharge applies
           store credit in the checkout order summary (as a Shopify gift card) — not here. So we
           state that plainly instead of showing a number the page can't honour. */
        totalsNote: cfg.checkoutNote !== undefined ? cfg.checkoutNote
          : 'Discount codes and any store credit are applied at checkout — you’ll see them in the order summary before you pay.',
        onExit: function () { global.location.href = cfg.exitUrl || '/'; },
        onAddToCart: commit,
        onNoChanges: function () { global.location.href = cfg.redirect || '/cart'; },
        onDiscardEdit: function () { global.location.href = cfg.redirect || '/cart'; },
        onEditExisting: function (a) {
          var box = boxes[boxes.length - 1]; if (!box) return;
          var st = toState(box, a.data);
          a.hydrate(st);
          if (st.dropped) a.toast(st.dropped + (st.dropped === 1 ? ' item is' : ' items are') + ' no longer available and wasn’t carried over.');
          try { history.replaceState(null, '', addParam(global.location.href, EDIT_PARAM, box.id)); } catch (e) { }
        },
        onChange: function (snap) { try { document.dispatchEvent(new CustomEvent('nrbox:change', { detail: snap })); } catch (e) { } }
      });
      global.NRBoxApp = app;

      // Deep link: /products/custom-box?edit=<bundleId> boots straight into edit mode
      if (target) {
        var st2 = toState(target, data);
        app.hydrate(st2);
        if (st2.dropped) app.toast(st2.dropped + (st2.dropped === 1 ? ' item is' : ' items are') + ' no longer available and wasn’t carried over.');
      }

      var wasUnlocked = false;
      document.addEventListener('nrbox:change', function (e) { if (e.detail && e.detail.totals && e.detail.totals.unlocked && !wasUnlocked) { wasUnlocked = true; try { document.dispatchEvent(new CustomEvent('nrbox:unlocked', { detail: e.detail })); } catch (x) { } } });
    }).catch(function (e) { fail('loadBundleData failed: ' + (e && e.message ? e.message : e)); });
  });

  function addParam(url, k, v) {
    var u = url.split('#')[0], hash = url.indexOf('#') > -1 ? url.slice(url.indexOf('#')) : '';
    var sep = u.indexOf('?') > -1 ? '&' : '?';
    u = u.replace(new RegExp('([?&])' + k + '=[^&]*'), '$1' + k + '=' + encodeURIComponent(v));
    if (u.indexOf(k + '=') < 0) u += sep + k + '=' + encodeURIComponent(v);
    return u + hash;
  }
})(window);
